import React, { createContext, useContext, useEffect, useState, useReducer } from 'react'
import * as Phoenix from 'phoenix'
import * as R from 'ramda'
import { Order, OrderWorkflowEvent, OrderWorkflowLog } from '../model-order'
const client = require('api/auth')

export const OrderAPI5Ctx = createContext<Phoenix.Socket | null>(null)
export const PresenceCtx = createContext<PresenceInfo>({})

export function OrderAPI5(props: { svc: string; children: any }) {
  const token = client.getAuthResponse() ? client.getAuthResponse().access_token : null
  const orderAPI5 = useOrderAPI5(token, props.svc)
  const presence: PresenceInfo = usePresenceChannel(orderAPI5)
  if (orderAPI5) {
    return (
      <OrderAPI5Ctx.Provider value={orderAPI5}>
        <PresenceCtx.Provider value={presence}>{props.children}</PresenceCtx.Provider>
      </OrderAPI5Ctx.Provider>
    )
  } else {
    return null
  }
}
const mpfx = '[WS][ORDERS API5]'
function useOrderAPI5(user_token: string, ordersSvcAPI5: string): Phoenix.Socket | null {
  const [socket, setSocket] = useState<Phoenix.Socket>(
    new Phoenix.Socket(ordersSvcAPI5 + '/orders/socket', {
      params: { token: user_token },
      logger: process.env.NODE_ENV == 'development' ? wsConsoleLogger : null,
    })
  )
  useEffect(() => {
    if (user_token && !socket.isConnected()) {
      socket.onError(() => console.error(`${mpfx} Failed to connect to ${ordersSvcAPI5}`))
      socket.onOpen(() => console.info('${mpfs} Socket connected'))
      socket.onClose(() => console.log('${mpfx} Closed socket connection'))
      console.log('[WS] Connecting to socket ', ordersSvcAPI5 + '/orders/socket')
      socket.connect()
    }
    return () => {
      if (socket) {
        console.log('[WS] Disconnecting from ', ordersSvcAPI5 + '/orders/socket')
        socket.disconnect()
      }
    }
  }, [user_token])
  return socket
}

function wsConsoleLogger(kind: string, msg: string, data: any) {
  if (process.env.NODE_ENV == 'development') console.log('[WS] ', kind, msg, data)
}

function wsNullLogger() {}

export interface PresenceInfo {
  [key: string]: {
    metas: {
      user_id: number
      username: string
      order: string
    }[]
  }
}

export function useOrdersPresence() {
  const presence = useContext(PresenceCtx)
  return presence
}

function usePresenceChannel(socket: Phoenix.Socket | null): PresenceInfo {
  const channelName = `orders`
  const [presence, setPresence] = useState<PresenceInfo>({})

  useEffect(() => {
    let channel: Phoenix.Channel | null = null
    if (socket) {
      channel = socket.channel(channelName)
      const phx_presence = new Phoenix.Presence(channel)

      channel.onError((reason?: any) => console.error(`${mpfx} Error on channel ${channelName}`, reason))
      channel.onClose(() => console.info(console.log(`${mpfx} Left channel ${channelName}`)))

      phx_presence.onSync(() => {
        setPresence(phx_presence.list((key: string, ps: any) => R.assoc(key, ps, {})).reduce(R.mergeDeepRight, {}))
      })

      channel
        .join()
        .receive('ok', (response?: any) => {
          console.log(`${mpfx} Joined channel ${channelName}`, response)
        })
        .receive('error', (response?: any) => console.error(`${mpfx} Error on channel ${channelName}`, response))
        .receive('timeout', () => console.log(`Networking issue. Still waiting... on ${channelName}`))
    }
    return () => {
      if (channel) {
        console.log('[WS] Leaving channel ', channelName)
        channel.leave()
      }
    }
  }, [socket])
  return presence
}

// ORDER CTX
// ****************************************************************
interface OrderChannel {
  nodeActions: NodeChangedEvent | null
  logs: OrderWorkflowLog[]
  events: OrderWorkflowEvent[]
  channel: Phoenix.Channel | null
}

export interface NodeChangedEvent {
  node: string
  transitions: string[]
}

interface OrderStatusAction {
  type: string
  log?: OrderWorkflowLog
  event?: OrderWorkflowEvent
  nodeActions?: NodeChangedEvent
  channel?: Phoenix.Channel
}

const OrderCtx = createContext<OrderChannel | null>(null)

export function OrderStatus(props: { channel: OrderChannel; children: any }) {
  return <OrderCtx.Provider value={props.channel}>{props.children}</OrderCtx.Provider>
}

export function useOrderChannel() {
  const orderCtx = useContext(OrderCtx)
  return orderCtx
}

export function pushToOrderChannel(orderChannel: OrderChannel, msg: string, payload: any): Phoenix.Push {
  if (orderChannel.channel) return orderChannel.channel.push(msg, payload)
  else throw 'Channel is not connected'
}

export function getOrderLogs(orderChannel: OrderChannel, order: Order) {
  return R.concat(order.order_workflow.logs, orderChannel.logs)
}

export function getOrderEvents(orderChannel: OrderChannel, order: Order) {
  return R.concat(order.order_workflow.history, orderChannel.events)
}

export function getOrderActions(orderChannel: OrderChannel): OrderChannel['nodeActions'] {
  return orderChannel.nodeActions
}

export function useOrderStatusConnect(client: string, order: Order): OrderChannel {
  const channelName = `order:${order.uuid}`
  const socket = useContext(OrderAPI5Ctx)

  const updateLogsAndEvents = (state: OrderChannel, action: OrderStatusAction): OrderChannel => {
    switch (action.type) {
      case 'SET_NODE_ACTIONS':
        return { ...state, nodeActions: action.nodeActions ? action.nodeActions : null }
      case 'ADD_EVENT':
        return { ...state, events: action.event ? R.append(action.event, state.events) : state.events }
      case 'ADD_LOG':
        return { ...state, logs: action.log ? R.append(action.log, state.logs) : state.logs }
      case 'SET_CHANNEL':
        return { ...state, channel: action.channel ? action.channel : null }
      default:
        return state
    }
  }

  const [state, dispatch] = useReducer(updateLogsAndEvents, {
    logs: [],
    events: [],
    nodeActions: null,
    channel: null,
  })

  useEffect(() => {
    if (socket) {
      let channel = socket.channel(channelName)
      channel.onError((reason?: any) => console.error(`${mpfx}[${client}] Error on channel ${channelName}`, reason))
      channel.onClose(() => console.info(console.log(`${mpfx}[${client}]  Left channel ${channelName}`)))

      channel.on('node_changed', (nodeActions) => dispatch({ type: 'SET_NODE_ACTIONS', nodeActions }))
      channel.on('outcome', (event) => dispatch({ type: 'ADD_EVENT', event }))
      channel.on('transition', (log) => dispatch({ type: 'ADD_LOG', log }))

      dispatch({ type: 'SET_CHANNEL', channel })

      channel
        .join()
        .receive('ok', (nodeActions?: any) => {
          console.log(`${mpfx}[${client}]  Joined channel ${channelName}`, nodeActions)
          dispatch({ type: 'SET_NODE_ACTIONS', nodeActions })
        })
        .receive('error', (response?: any) =>
          console.error(`${mpfx}[${client}]  Error on channel ${channelName}`, response)
        )
        .receive('timeout', () => console.log(`[${client}] Networking issue. Still waiting... on ${channelName}`))
    }
    return () => {
      if (state.channel) {
        console.log('[WS][${client}]  Leaving channel ', channelName)
        state.channel.leave()
        dispatch({ type: 'SET_CHANNEL', channel: undefined })
      }
    }
  }, [socket])
  return state
}
